import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../store/slices/authSlice';
import { Toast } from '../components/Toast';
import StatsCards from '../components/admin/StatsCards';
import OverviewTab from '../components/admin/OverviewTab';
import UsersTab from '../components/admin/UsersTab';
import ProfilesTab from '../components/admin/ProfilesTab';
import PromptsTab from '../components/admin/PromptsTab';
import BoardsTab from '../components/admin/BoardsTab';
import CommentsTab from '../components/admin/CommentsTab';
import ActivityTab from '../components/admin/ActivityTab';

function Admin() {
  const currentUser = useSelector(selectCurrentUser);
  // Get token from Redux state
  const token = useSelector((state) => state.auth.token);
  
  console.log('Admin - Current User:', currentUser);
  console.log('Admin - Token from Redux:', token);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [pins, setPins] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [boards, setBoards] = useState([]);
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination states
  const [currentPinsPage, setCurrentPinsPage] = useState(1);
  const [currentCommentsPage, setCurrentCommentsPage] = useState(1);
  const [currentActivitiesPage, setCurrentActivitiesPage] = useState(1);
  const [totalPinsPages, setTotalPinsPages] = useState(1);
  const [totalCommentsPages, setTotalCommentsPages] = useState(1);
  const [totalActivitiesPages, setTotalActivitiesPages] = useState(1);
  const PINS_PER_PAGE = 20;
  const COMMENTS_PER_PAGE = 20;
  const ACTIVITIES_PER_PAGE = 20;
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [commentTab, setCommentTab] = useState('all');
  const [commentStatusFilter, setCommentStatusFilter] = useState('all');
  const [searchComments, setSearchComments] = useState('');
  const [activityTypeFilter, setActivityTypeFilter] = useState('');
  const [searchActivities, setSearchActivities] = useState('');
  const [topLikedPrompts, setTopLikedPrompts] = useState([]);
  
  // Selected items for bulk actions
  const [selectedPins, setSelectedPins] = useState(new Set());

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'prompts') {
      loadPins(currentPinsPage, statusFilter);
    } else if (activeTab === 'boards') {
      fetchBoards();
    } else if (activeTab === 'comments') {
      loadComments(1); // Reset to page 1 when entering comments tab
    } else if (activeTab === 'activity') {
      loadActivities();
    }
  }, [activeTab, currentPinsPage, statusFilter, commentTab]); // Add commentTab dependency

  const loadDashboard = async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchUsers(),
      fetchPins(1, ''),
      fetchProfiles(),
      fetchTopLikedPrompts()
    ]);
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      console.log('fetchStats - Using token:', token);
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username })
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('fetchStats error:', error);
        throw new Error('Failed');
      }
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('fetchStats catch:', error);
      Toast.error('Không thể tải thống kê');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPins = async (page = 1, status = '') => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/pins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username, page, limit: PINS_PER_PAGE, status })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setPins(data.pins || []);
      setTotalPinsPages(data.totalPages || 1);
      setCurrentPinsPage(data.page || 1);
    } catch (error) {
      console.error(error);
    }
  };

  const loadPins = (page, status) => {
    fetchPins(page, status);
  };

  const fetchProfiles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/user-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setProfiles(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchBoards = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/board-stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setBoards(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTopLikedPrompts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pins`);
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      // Sort by likes and get top 10
      const sorted = data.sort((a, b) => (b.likes || 0) - (a.likes || 0)).slice(0, 10);
      setTopLikedPrompts(sorted);
    } catch (error) {
      console.error(error);
    }
  };

  const exportProfilesCSV = () => {
    const headers = ['Username', 'Loại người dùng', 'Sở thích AI', 'Chuyên môn', 'Mục đích', 'Ngày hoàn thành'];
    const rows = profiles.map(p => [
      p.username,
      p.userType,
      p.aiInterest,
      p.expertise,
      p.purpose,
      new Date(p.completedAt).toLocaleDateString('vi-VN')
    ]);
    
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `user-profiles-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    Toast.success('Đã xuất dữ liệu thành công!');
  };

  const loadComments = async (page = 1) => {
    try {
      const endpoint = commentTab === 'violations' ? '/admin/comments/violations' :
                      commentTab === 'reported' ? '/admin/comments/reported' :
                      '/admin/comments/all';
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          username: currentUser.username, 
          page, 
          limit: COMMENTS_PER_PAGE,
          status: commentStatusFilter,
          search: searchComments
        })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setComments(data.comments || data);
      setTotalCommentsPages(data.pagination?.totalPages || 1);
      setCurrentCommentsPage(page);
    } catch (error) {
      console.error(error);
    }
  };

  const loadActivities = async (page = 1) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/activity-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          username: currentUser.username, 
          page, 
          limit: ACTIVITIES_PER_PAGE,
          type: activityTypeFilter,
          search: searchActivities
        })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setActivities(data.logs || []);
      setTotalActivitiesPages(data.pagination?.totalPages || 1);
      setCurrentActivitiesPage(page);
    } catch (error) {
      console.error(error);
    }
  };

  // Action handlers
  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Xóa user "${username}"? Tất cả prompts sẽ bị xóa.`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username, userId })
      });
      if (!response.ok) throw new Error('Failed');
      Toast.success('Đã xóa user!');
      loadDashboard();
    } catch (error) {
      Toast.error('Lỗi khi xóa user!');
    }
  };

  const makeAdmin = async (userId) => {
    if (!window.confirm('Cấp quyền admin cho user này?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/make-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username, userId })
      });
      if (!response.ok) throw new Error('Failed');
      Toast.success('Đã cấp quyền admin!');
      loadDashboard();
    } catch (error) {
      Toast.error('Lỗi khi cấp quyền!');
    }
  };

  const approvePin = async (pinId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/pins/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username, pinId })
      });
      if (!response.ok) throw new Error('Failed');
      Toast.success('Đã duyệt prompt!');
      loadPins(currentPinsPage, statusFilter);
    } catch (error) {
      Toast.error('Lỗi khi duyệt!');
    }
  };

  const rejectPin = async (pinId) => {
    const reason = prompt('Lý do từ chối (tùy chọn):');
    if (reason === null) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/pins/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username, pinId, reason })
      });
      if (!response.ok) throw new Error('Failed');
      Toast.success('Đã từ chối prompt!');
      loadPins(currentPinsPage, statusFilter);
    } catch (error) {
      Toast.error('Lỗi khi từ chối!');
    }
  };

  const deletePin = async (pinId) => {
    if (!window.confirm('Xóa prompt này?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/pins/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username, pinId })
      });
      if (!response.ok) throw new Error('Failed');
      Toast.success('Đã xóa prompt!');
      loadPins(currentPinsPage, statusFilter);
    } catch (error) {
      Toast.error('Lỗi khi xóa!');
    }
  };

  const toggleHideComment = async (pinId, commentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/comments/${pinId}/${commentId}/toggle-hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      Toast.success(data.message);
      loadComments(currentCommentsPage);
    } catch (error) {
      Toast.error('Lỗi!');
    }
  };

  const deleteComment = async (pinId, commentId) => {
    if (!window.confirm('Xóa bình luận này?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/comments/${pinId}/${commentId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username })
      });
      if (!response.ok) throw new Error('Failed');
      Toast.success('Đã xóa bình luận!');
      loadComments(currentCommentsPage);
    } catch (error) {
      Toast.error('Lỗi!');
    }
  };

  const markViolation = async (pinId, commentId) => {
    const reason = prompt('Lý do vi phạm:');
    if (!reason) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/comments/${pinId}/${commentId}/mark-violation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username, reason })
      });
      if (!response.ok) throw new Error('Failed');
      Toast.success('Đã đánh dấu vi phạm!');
      loadComments(currentCommentsPage);
    } catch (error) {
      Toast.error('Lỗi!');
    }
  };

  const clearViolation = async (pinId, commentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/comments/${pinId}/${commentId}/clear-violation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: currentUser.username })
      });
      if (!response.ok) throw new Error('Failed');
      Toast.success('Đã xóa đánh dấu!');
      loadComments(currentCommentsPage);
    } catch (error) {
      Toast.error('Lỗi!');
    }
  };

  // Bulk actions
  const bulkApprove = async () => {
    if (selectedPins.size === 0) return;
    if (!window.confirm(`Duyệt ${selectedPins.size} prompts?`)) return;
    let count = 0;
    for (const pinId of selectedPins) {
      try {
        await fetch(`${API_BASE_URL}/admin/pins/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ username: currentUser.username, pinId })
        });
        count++;
      } catch (e) {}
    }
    Toast.success(`Đã duyệt ${count} prompts!`);
    setSelectedPins(new Set());
    loadPins(currentPinsPage, statusFilter);
  };

  const bulkReject = async () => {
    if (selectedPins.size === 0) return;
    const reason = prompt('Lý do từ chối:');
    if (reason === null) return;
    if (!window.confirm(`Từ chối ${selectedPins.size} prompts?`)) return;
    let count = 0;
    for (const pinId of selectedPins) {
      try {
        await fetch(`${API_BASE_URL}/admin/pins/reject`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ username: currentUser.username, pinId, reason })
        });
        count++;
      } catch (e) {}
    }
    Toast.success(`Đã từ chối ${count} prompts!`);
    setSelectedPins(new Set());
    loadPins(currentPinsPage, statusFilter);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-blue-200 dark:border-blue-900 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Đang tải Dashboard...</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Vui lòng đợi trong giây lát</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Header with Gradient */}
      
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <StatsCards stats={stats} />

        {/* Tabs Container with Glass Effect */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Tabs Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
            <div className="flex overflow-x-auto no-scrollbar">
              {[
                { id: 'overview', icon: 'dashboard', label: 'Tổng quan', color: 'blue' },
                { id: 'users', icon: 'group', label: 'Users', color: 'indigo' },
                { id: 'profiles', icon: 'badge', label: 'Hồ sơ', color: 'purple' },
                { id: 'prompts', icon: 'description', label: 'Prompts', color: 'pink', badge: stats.pendingPins },
                { id: 'boards', icon: 'dashboard', label: 'Boards', color: 'cyan' },
                { id: 'comments', icon: 'comment', label: 'Bình luận', color: 'orange' },
                { id: 'activity', icon: 'history', label: 'Hoạt động', color: 'green' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-4 sm:px-6 py-3 sm:py-4 font-semibold whitespace-nowrap flex items-center gap-2 transition-all group ${
                    activeTab === tab.id 
                      ? 'text-' + tab.color + '-600 dark:text-' + tab.color + '-400' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <span className={`material-symbols-rounded text-[20px] transition-transform group-hover:scale-110 ${
                    activeTab === tab.id ? 'animate-bounce-in' : ''
                  }`}>
                    {tab.icon}
                  </span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  
                  {/* Badge for pending items */}
                  {tab.badge > 0 && (
                    <span className="absolute -top-1 -right-1 sm:relative sm:top-0 sm:right-0 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-lg animate-pulse">
                      {tab.badge}
                    </span>
                  )}
                  
                  {/* Active indicator */}
                  {activeTab === tab.id && (
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-${tab.color}-500 to-${tab.color}-600 rounded-t-full animate-slide-in-left`}></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content with Animation */}
          <div className="animate-fade-in">
            {activeTab === 'overview' && <OverviewTab setActiveTab={setActiveTab} setStatusFilter={setStatusFilter} topLikedPrompts={topLikedPrompts} stats={stats} />}
            {activeTab === 'users' && <UsersTab users={users} deleteUser={deleteUser} makeAdmin={makeAdmin} />}
            {activeTab === 'profiles' && <ProfilesTab profiles={profiles} exportProfilesCSV={exportProfilesCSV} />}
            {activeTab === 'prompts' && (
              <PromptsTab
                pins={pins}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                currentPinsPage={currentPinsPage}
                setCurrentPinsPage={setCurrentPinsPage}
                totalPinsPages={totalPinsPages}
                selectedPins={selectedPins}
                setSelectedPins={setSelectedPins}
                approvePin={approvePin}
                rejectPin={rejectPin}
                deletePin={deletePin}
                bulkApprove={bulkApprove}
                bulkReject={bulkReject}
              />
            )}
            {activeTab === 'boards' && <BoardsTab boards={boards} />}
            {activeTab === 'comments' && (
              <CommentsTab
                comments={comments}
                commentTab={commentTab}
                setCommentTab={setCommentTab}
                commentStatusFilter={commentStatusFilter}
                setCommentStatusFilter={setCommentStatusFilter}
                searchComments={searchComments}
                setSearchComments={setSearchComments}
                currentCommentsPage={currentCommentsPage}
                setCurrentCommentsPage={setCurrentCommentsPage}
                totalCommentsPages={totalCommentsPages}
                loadComments={loadComments}
                toggleHideComment={toggleHideComment}
                deleteComment={deleteComment}
                markViolation={markViolation}
                clearViolation={clearViolation}
              />
            )}
            {activeTab === 'activity' && (
              <ActivityTab
                activities={activities}
                activityTypeFilter={activityTypeFilter}
                setActivityTypeFilter={setActivityTypeFilter}
                searchActivities={searchActivities}
                setSearchActivities={setSearchActivities}
                currentActivitiesPage={currentActivitiesPage}
                setCurrentActivitiesPage={setCurrentActivitiesPage}
                totalActivitiesPages={totalActivitiesPages}
                loadActivities={loadActivities}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
